import { BookingOrder, BookingOrderStatus } from "models/bookingOrder"
import { CellProps, Column } from "react-table"

import { ColorMap } from "utils/color"
import { ReactComponent as DeleteIcon } from "assets/delete.svg"
import { ReactComponent as CancelIcon } from "assets/close.svg"
import { ReactComponent as EyeIcon } from "assets/invoice/eye.svg"
import { ReactComponent as ExportIcon } from "assets/export.svg"
import { Link } from "components/Link"
import MoreActions from "components/MoreActions"
import { MorePopover } from "components/MorePopover"
import { Status } from "components/Status"
import { getVendorOptionLabel } from "./utils"
import { useI18n } from "providers/I18n"
import { useAuthenticated } from "providers/A12n"
import { FF_CANCEL_BO } from "../../BookingOrders/constants"

type ColumnState = Array<
  Column<BookingOrder> & {
    header: string
    fixed?: boolean
    default: boolean
    variant?: string
    sortable?: boolean
  }
>

export const COLUMNS = (
  t: (k: string) => string,
  dateFormatter: (d: string) => string,
  onShowCancellationDetail: (id: string) => void,
  onCancel?: (id: string, name: string) => void,
  onDownloadCancelledBo?: (id: string, name: string | undefined) => void,
  onDelete?: (id: string) => void,
  id?: string,
): ColumnState => {
  const commonColumns = [
    {
      fixed: true,
      default: true,
      sortable: false,
      header: "bo.list.number",
      id: "number",
      Cell: function (props: CellProps<BookingOrder>) {
        const { number, id: boId, mediaPlanId } = props.row.original
        return (
          <Link
            color="primary"
            to={`/booking-orders/${mediaPlanId}/${boId}/table`}
          >
            {number}
          </Link>
        )
      },
    },
    {
      default: false,
      sortable: false,
      header: "bo.list.bookingOrder",
      id: "name",
      Cell: function (props: CellProps<BookingOrder>) {
        const { name } = props.row.original
        return <span>{name}</span>
      },
    },
    {
      default: true,
      sortable: false,
      header: "bo.list.clientName",
      accessor: "clientName",
      Cell: function (props: CellProps<BookingOrder>) {
        const { clientName } = props.row.original
        return <span>{clientName}</span>
      },
    },
    {
      default: true,
      sortable: false,
      header: "bo.list.status",
      accessor: "status",
      Cell: StatusCell,
    },
    {
      default: false,
      sortable: false,
      header: "bo.list.vendor",
      accessor: "vendors",
      Cell: function (props: CellProps<BookingOrder>) {
        const { vendors = [] } = props.row.original
        const vendorsMapped = vendors?.map((v) => ({
          name: getVendorOptionLabel(v),
        }))
        return <ItemsToShow items={vendorsMapped} name="bo.list.vendors" />
      },
    },
    {
      default: false,
      sortable: false,
      header: "bo.list.vendorProducts",
      accessor: "vendorProducts",
      Cell: function (props: CellProps<BookingOrder>) {
        const { channels = [] } = props.row.original
        const channelsMapped = channels?.map((c) => ({
          name: c?.name,
        }))
        return (
          <ItemsToShow items={channelsMapped} name="bo.list.vendorProducts" />
        )
      },
    },
    {
      default: false,
      sortable: false,
      header: "bo.list.mediaTypes",
      accessor: "mediaTypes",
      Cell: function (props: CellProps<BookingOrder>) {
        const { mediaTypes = [] } = props.row.original
        const mediaTypesMapped = mediaTypes?.map((c) => ({
          name: c?.name,
        }))
        return (
          <ItemsToShow items={mediaTypesMapped} name="bo.list.mediaTypes" />
        )
      },
    },
    {
      default: true,
      sortable: false,
      header: "bo.list.startDate",
      accessor: "startDate",
      Cell: function (props: CellProps<BookingOrder>) {
        const { startDate } = props.row.original
        return <span>{startDate ? dateFormatter(startDate) : ""}</span>
      },
    },
    {
      default: true,
      sortable: false,
      header: "bo.list.endDate",
      accessor: "endDate",
      Cell: function (props: CellProps<BookingOrder>) {
        const { endDate } = props.row.original
        return <span>{endDate ? dateFormatter(endDate) : ""}</span>
      },
    },
    {
      default: true,
      sortable: false,
      header: "bo.list.createdDate",
      accessor: "createdDate",
      Cell: function (props: CellProps<BookingOrder>) {
        const { createdDate } = props.row.original
        return (
          <span>{createdDate ? dateFormatter(String(createdDate)) : ""}</span>
        )
      },
    },
    {
      default: true,
      sortable: false,
      header: "bo.list.createdBy",
      accessor: "createdByDetails",
      Cell: function (props: CellProps<BookingOrder>) {
        const { createdByDetails } = props.row.original
        const { firstName, lastName } = createdByDetails
        return <span>{[firstName, lastName].join(" ")}</span>
      },
    },
    {
      default: true,
      sortable: false,
      header: "bo.list.updatedDate",
      accessor: "updatedDate",
      Cell: function (props: CellProps<BookingOrder>) {
        const { updatedDate } = props.row.original
        return (
          <span>{updatedDate ? dateFormatter(String(updatedDate)) : ""}</span>
        )
      },
    },
    {
      default: true,
      sortable: false,
      header: "bo.list.rutNumber",
      accessor: "rutNumber",
      Cell: function (props: CellProps<BookingOrder>) {
        const { vendors = [] } = props.row.original
        const validRutNumbers = vendors.reduce<string[]>(
          (acc, { mediaOwner }) => {
            const rutNumber = mediaOwner.rutNumber
            if (rutNumber) acc.push(rutNumber)
            return acc
          },
          [],
        )
        return <span>{validRutNumbers.join(",")}</span>
      },
    },
    {
      id: "actions",
      fixed: true,
      default: true,
      sortable: false,
      align: "right",
      header: "bo.list.actions",
      Cell: function (props: CellProps<BookingOrder>) {
        const { id, status, availableViews, name, number } = props.row.original
        const { hasAuthority } = useAuthenticated()
        const hasClientAgencyView = availableViews?.some(
          (v) => v?.type === "CLIENT_AGENCY" && v?.hasAccess,
        )
        const moreActionsOptions = []

        if (status === "CANCELLED") {
          moreActionsOptions.push(
            {
              label: t("bo.cancel.download"),
              icon: <ExportIcon />,
              onClick: () => {
                if (onDownloadCancelledBo) onDownloadCancelledBo(id, number)
              },
            },
            {
              label: t("bo.cancel.view.details"),
              icon: <EyeIcon />,
              onClick: () => {
                onShowCancellationDetail(id)
              },
            },
          )
        }

        if (FF_CANCEL_BO) {
          moreActionsOptions.push({
            hidden:
              !hasAuthority(["AUTH_DELETE_BOOKING_ORDER"]) ||
              status !== "BOOKED" ||
              !hasClientAgencyView,
            label: t("bo.list.cancel.action"),
            icon: <CancelIcon />,
            onClick: () => {
              if (onCancel) {
                onCancel(id, name)
              }
            },
          })
        } else {
          moreActionsOptions.push({
            hidden:
              !hasAuthority(["AUTH_DELETE_BOOKING_ORDER"]) ||
              status === "POSTED" ||
              !hasClientAgencyView,
            label: t("bo.list.delete.action"),
            icon: <DeleteIcon />,
            onClick: () => {
              if (onDelete) {
                onDelete(id)
              }
            },
          })
        }

        return (
          <MoreActions
            testId="more-actions-table-bo-list-button"
            options={moreActionsOptions}
          />
        )
      },
    },
  ] as ColumnState

  const mediaPlanColumn = [
    {
      default: true,
      sortable: false,
      header: "bo.list.mediaPlan",
      accessor: "mediaPlanName",
    },
  ] as ColumnState

  return !id
    ? [
        ...commonColumns.slice(0, 6),
        ...mediaPlanColumn,
        ...commonColumns.slice(6),
      ]
    : commonColumns
}

function ItemsToShow<T extends Record<string, any>>({
  items,
  name,
}: {
  items: T[]
  name: string
}) {
  return (
    <span className="BodyBody-2BlackRegular">
      {items
        ?.map((i) => i.name)
        .slice(0, 1)
        .join(", ")}
      <MorePopover data={items} label={name} name="name" slice={1} />
    </span>
  )
}

function StatusCell(props: CellProps<BookingOrder, BookingOrderStatus>) {
  const { value } = props
  const { t } = useI18n()
  return <Status color={ColorMap[value]}>{t(`status.${value ?? ""}`)}</Status>
}
